import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loading, ErrorBox } from "@/components/common";
import { ROLE_LABEL } from "@/lib/labels";
import { fmtDateTime } from "@/lib/time";
import { selectCls } from "@/pages/Take5New";
import { Pencil, UserMinus, UserPlus, KeyRound } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ProfileForm } from "@/components/ProfileForm";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const AddMember = ({ onAdded }) => {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("operateur");
  const add = async () => {
    try {
      await api.post("/admin/users", { email, role });
      toast.success("Compte ajouté à ce workplace.");
      setEmail("");
      onAdded();
    } catch (e) { toast.error(errMessage(e)); }
  };
  return (
    <div className="mb-4 flex flex-col gap-2 rounded-lg border border-border p-3 sm:flex-row" data-testid="add-member-form">
      <Input data-testid="add-member-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="E-mail d'un compte Safe Start existant" className="h-11" />
      <select data-testid="add-member-role" className={`${selectCls} h-11 sm:w-48`} value={role} onChange={(e) => setRole(e.target.value)}>
        {Object.entries(ROLE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
      <Button data-testid="add-member-submit" disabled={!email.trim()} onClick={add} className="h-11">Ajouter au workplace</Button>
    </div>
  );
};

const EMPTY = { first_name: "", last_name: "", email: "", phone: "", role: "operateur" };
const CREATE_FIELDS = [["first_name", "Prénom"], ["last_name", "Nom"], ["email", "E-mail (identifiant)"], ["phone", "Téléphone (facultatif)"]];

const CreateMember = ({ onAdded }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [manual, setManual] = useState(null);
  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/users/create", form);
      const email = form.email.trim().toLowerCase();
      if (data.email_sent) toast.success(`Compte créé : un e-mail avec un mot de passe provisoire a été envoyé à ${email}.`);
      else setManual({ email, password: data.temporary_password, reason: data.email_error });
      setForm(EMPTY);
      setOpen(false);
      onAdded();
    } catch (err) { toast.error(errMessage(err)); } finally { setBusy(false); }
  };
  return (
    <>
      <Button data-testid="create-member-open" onClick={() => setOpen(true)} className="mb-4 h-11"><UserPlus className="mr-2 h-4 w-4" /> Créer un utilisateur</Button>
      <Dialog open={!!manual} onOpenChange={(o) => !o && setManual(null)}>
        <DialogContent data-testid="create-member-manual-dialog">
          <DialogHeader><DialogTitle>Compte créé — e-mail non envoyé</DialogTitle></DialogHeader>
          <p className="text-sm">L'e-mail n'a pas pu être envoyé à <strong>{manual?.email}</strong>{manual?.reason ? ` (${manual.reason})` : ""}. Transmettez vous-même ce mot de passe provisoire à la personne : il ne sera plus affiché ensuite.</p>
          <p data-testid="create-member-temp-password" className="select-all rounded-md border border-amber-500/60 bg-amber-500/10 p-3 text-center font-data text-lg font-bold">{manual?.password}</p>
          <p className="text-xs text-muted-foreground">À sa première connexion, elle devra choisir son propre mot de passe.</p>
          <Button data-testid="create-member-manual-close" onClick={() => setManual(null)}>J'ai noté le mot de passe</Button>
        </DialogContent>
      </Dialog>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="create-member-dialog">
          <DialogHeader><DialogTitle>Créer un utilisateur</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-3">
            {CREATE_FIELDS.map(([k, l]) => (
              <div key={k}>
                <label htmlFor={`cm-${k}`} className="text-sm font-semibold">{l}</label>
                <Input id={`cm-${k}`} data-testid={`create-member-${k}`} type={k === "email" ? "email" : "text"} required={k !== "phone"} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1 h-11" />
              </div>
            ))}
            <div>
              <label htmlFor="cm-role" className="text-sm font-semibold">Rôle</label>
              <select id="cm-role" data-testid="create-member-role" className={`${selectCls} mt-1 h-11`} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                {Object.entries(ROLE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <p className="text-xs text-muted-foreground">La personne reçoit un e-mail avec un mot de passe provisoire, à remplacer dès sa première connexion. Si l'e-mail est refusé, le compte est quand même créé et le mot de passe provisoire s'affiche pour que vous le transmettiez.</p>
            <Button type="submit" disabled={busy} data-testid="create-member-submit" className="h-11 w-full">Créer et envoyer l'e-mail</Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const UsersSection = () => {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [org, setOrg] = useState(null);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [pwUser, setPwUser] = useState(null);
  const load = () => {
    setError("");
    Promise.all([api.get("/admin/users"), api.get("/admin/organization")])
      .then(([u, o]) => { setRows(u.data); setOrg(o.data); })
      .catch((e) => setError(errMessage(e)));
  };
  useEffect(() => { load(); }, []);
  const update = async (u, patch) => {
    try {
      await api.put(`/admin/users/${u.id}`, { role: u.role, active: u.active !== false, ...patch });
      toast.success("Utilisateur mis à jour.");
      load();
    } catch (e) { toast.error(errMessage(e)); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Retirer ${u.name} de ce workplace ? Son compte et ses Take 5 / inspections passés sont conservés.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Utilisateur retiré du workplace.");
      load();
    } catch (e) { toast.error(errMessage(e)); }
  };
  if (error) return <ErrorBox message={error} onRetry={load} />;
  if (!rows) return <Loading />;
  return (
    <div>
      <p className="mb-4 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm">Code d'inscription de l'organisation : <strong data-testid="org-join-code" className="font-data text-amber-200">{org?.join_code}</strong> — les nouveaux inscrits obtiennent le rôle Opérateur.</p>
      <CreateMember onAdded={load} />
      <AddMember onAdded={load} />
      <div className="space-y-2">
        {rows.map((u) => (
          <div key={u.id} data-testid={`user-row-${u.email}`} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card p-3">
            <div className="min-w-0"><p className="font-semibold">{u.name}</p><p className="text-xs text-muted-foreground">{u.email}{u.phone ? ` · ${u.phone}` : ""}</p></div>
            <div className="flex flex-wrap items-center gap-3">
              <select data-testid={`user-role-${u.email}`} className={`${selectCls} h-11 w-48`} value={u.role} disabled={u.id === user.id} onChange={(e) => update(u, { role: e.target.value })}>
                {Object.entries(ROLE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="h-5 w-5 accent-amber-500" disabled={u.id === user.id} checked={u.active !== false} onChange={(e) => update(u, { active: e.target.checked })} data-testid={`user-active-${u.email}`} /> Actif</label>
              <Button size="sm" variant="outline" data-testid={`user-edit-${u.email}`} onClick={() => setEditing(u)}><Pencil className="mr-1 h-3.5 w-3.5" /> Modifier</Button>
              {u.id !== user.id && <Button size="sm" variant="outline" data-testid={`user-password-${u.email}`} onClick={() => setPwUser(u)}><KeyRound className="mr-1 h-3.5 w-3.5" /> Mot de passe</Button>}
              {u.id !== user.id && <Button size="sm" variant="ghost" data-testid={`user-remove-${u.email}`} onClick={() => remove(u)} className="text-red-500 hover:bg-red-500/10"><UserMinus className="mr-1 h-3.5 w-3.5" /> Retirer</Button>}
            </div>
          </div>
        ))}
      </div>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-xl" data-testid="user-edit-dialog">
          <DialogHeader><DialogTitle>Modifier · {editing?.name}</DialogTitle></DialogHeader>
          {editing && <ProfileForm initial={editing} url={`/admin/users/${editing.id}/profile`} email={editing.email} testPrefix="user-edit" onSaved={() => { setEditing(null); load(); }} />}
        </DialogContent>
      </Dialog>
      <PasswordDialog u={pwUser} onClose={() => setPwUser(null)} />
    </div>
  );
};

const PasswordDialog = ({ u, onClose }) => {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const close = () => { setPw(""); setPw2(""); onClose(); };
  const save = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { toast.error("Les deux mots de passe ne correspondent pas."); return; }
    setBusy(true);
    try {
      await api.put(`/admin/users/${u.id}/password`, { password: pw });
      toast.success(`Mot de passe de ${u.name} modifié. Communiquez-le-lui : il devra le changer à sa prochaine connexion.`);
      close();
    } catch (err) { toast.error(errMessage(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!u} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="max-w-md" data-testid="user-password-dialog">
        <DialogHeader><DialogTitle>Nouveau mot de passe · {u?.name}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <Input type="password" autoComplete="new-password" minLength={8} required placeholder="Nouveau mot de passe (8 caractères min.)" value={pw} onChange={(e) => setPw(e.target.value)} className="h-11" data-testid="user-password-input" />
          <Input type="password" autoComplete="new-password" minLength={8} required placeholder="Confirmer le mot de passe" value={pw2} onChange={(e) => setPw2(e.target.value)} className="h-11" data-testid="user-password-confirm" />
          <p className="text-xs text-muted-foreground">Ses sessions ouvertes seront fermées (tous workplaces) et il devra choisir son propre mot de passe à la prochaine connexion.</p>
          <Button type="submit" disabled={busy} className="h-11 w-full" data-testid="user-password-submit">Enregistrer</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ACTION_LABEL = {
  "take5.create": "Création Take 5", "take5.authorize": "Autorisation Take 5", "take5.stop": "Arrêt du travail",
  "take5.revise": "Réévaluation Take 5", "take5.archive": "Archivage Take 5", "inspection.create": "Début d'inspection",
  "inspection.finalize": "Finalisation d'inspection", "equipment.block": "Blocage d'équipement",
  "equipment.status_change": "Changement d'état d'équipement", "admin.create": "Création (administration)",
  "admin.update": "Modification (administration)", "admin.user.update": "Modification d'utilisateur", "user.register": "Inscription",
  "user.password_reset_request": "Demande de réinitialisation (e-mail envoyé)", "user.password_reset_email_failed": "ÉCHEC d'envoi de l'e-mail de réinitialisation",
  "user.password_reset": "Mot de passe réinitialisé", "user.password_change": "Mot de passe changé (profil)", "admin.user.add": "Compte ajouté au workplace", "admin.user.create": "Utilisateur créé par l'administrateur", "admin.user.password": "Mot de passe modifié par l'administrateur", "admin.alert_settings": "Paramètres d'alerte modifiés", "admin.user.profile": "Profil utilisateur modifié", "admin.user.remove": "Utilisateur retiré du workplace", "admin.delete": "Élément supprimé", "take5.delete": "Take 5 supprimé", "take5.restore": "Take 5 restauré", "inspection.delete": "Inspection supprimée", "inspection.restore": "Inspection restaurée", "user.profile_update": "Profil modifié", "user.deletion_request": "Demande de suppression de compte", "user.join": "A rejoint le workplace", "team.members": "Membres d'équipe modifiés", "team.composition": "Équipe du jour (ancien)", "take5.complete": "Tâche terminée", "take5.complete_supervisor": "Tâche terminée (clôture superviseur)",
};

export const AuditSection = () => {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => { api.get("/admin/audit").then((r) => setRows(r.data)).catch((e) => setError(errMessage(e))); }, []);
  if (error) return <ErrorBox message={error} />;
  if (!rows) return <Loading />;
  return (
    <div className="space-y-1.5" data-testid="audit-list">
      <p className="mb-2 text-xs text-muted-foreground">Heures affichées dans le fuseau de votre appareil (le journal couvre plusieurs sites).</p>
      {rows.map((r) => (
        <div key={r.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-border bg-card px-3 py-2 text-sm">
          <span className="font-data text-xs text-muted-foreground">{fmtDateTime(r.at)}</span>
          <span className="font-semibold">{ACTION_LABEL[r.action] || r.action}</span>
          <span className="text-muted-foreground">{r.user_name}</span>
          <span className="truncate text-xs text-slate-400">{r.details?.name || r.details?.reason || r.details?.result || r.details?.equipment || (r.details?.changes ? `${r.target_type} : ${Object.keys(r.details.changes).join(", ") || "aucun changement"}` : "")}</span>
        </div>
      ))}
    </div>
  );
};
